import React, { useState, useEffect, useMemo } from "react"
import { Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from "reactstrap"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { getAllAdvances, generateAdvanceReturnCode, returnAdvance, createBalanceAdvance } from "../../api"
import TableContainer from "../../components/Common/TableContainer"

function BalanceAdvancesPage({ t }) {
  document.title = t("Balance Advances")

  const [advances, setAdvances] = useState([])
  const [filteredAdvances, setFilteredAdvances] = useState([])
  const [loadingAdvances, setLoadingAdvances] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Create advance modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [balanceType, setBalanceType] = useState("fiat")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Return modal
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [selectedAdvance, setSelectedAdvance] = useState(null)
  const [returnCode, setReturnCode] = useState("")
  const [returning, setReturning] = useState(false)
  const [returnError, setReturnError] = useState("")

  useEffect(() => {
    loadAdvances()
  }, [])

  // Filter advances when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredAdvances(advances)
      return
    }

    const searchLower = searchTerm.toLowerCase()
    const filtered = advances.filter(advance => {
      return (
        (advance.advanceNumber && advance.advanceNumber.toString().toLowerCase().includes(searchLower)) ||
        (advance.description && advance.description.toLowerCase().includes(searchLower)) ||
        (advance.balanceType && advance.balanceType.toLowerCase().includes(searchLower)) ||
        (advance.amount && advance.amount.toString().toLowerCase().includes(searchLower))
      )
    })
    setFilteredAdvances(filtered)
  }, [searchTerm, advances])

  const loadAdvances = () => {
    setLoadingAdvances(true)
    getAllAdvances()
      .then((data) => {
        setAdvances(data)
        setFilteredAdvances(data)
      })
      .catch((error) => {
        console.error("Error loading advances:", error)
      })
      .finally(() => {
        setLoadingAdvances(false)
      })
  }

  const handleCreateClick = () => {
    setAmount("")
    setDescription("")
    setBalanceType("fiat")
    setShowCreateModal(true)
  }

  const handleCreateSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t("Please enter a valid amount"))
      return
    }
    if (!description || description.trim() === "") {
      alert(t("Please enter a description"))
      return
    }

    setShowCreateModal(false)
    setShowConfirmModal(true)
  }

  const handleConfirmCreate = () => {
    setSubmitting(true)
    setShowConfirmModal(false)

    createBalanceAdvance({
      amount: parseFloat(amount),
      description,
      balanceType
    })
      .then(() => {
        alert(t("Advance added successfully!"))
        setAmount("")
        setDescription("")
        setBalanceType("fiat")
        loadAdvances()
      })
      .catch((error) => {
        alert(t("Failed to add advance: ") + error.message)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleReturnClick = (advance) => {
    setSelectedAdvance(advance)
    setReturnCode("")
    setReturnError("")

    generateAdvanceReturnCode(advance.advanceNumber)
      .then(() => {
        setShowReturnModal(true)
      })
      .catch((error) => {
        alert(t("Failed to generate return code. Please try again."))
        console.error("Error generating return code:", error)
      })
  }

  const handleReturnConfirm = () => {
    if (!returnCode || returnCode.trim() === "") {
      setReturnError(t("Please enter the code"))
      return
    }

    setReturning(true)
    setReturnError("")

    returnAdvance(selectedAdvance.advanceNumber, returnCode)
      .then(() => {
        setShowReturnModal(false)
        alert(t("Advance returned successfully!"))
        loadAdvances()
      })
      .catch((error) => {
        setReturnError(t("Invalid code or return failed. Please try again."))
        console.error("Error returning advance:", error)
      })
      .finally(() => {
        setReturning(false)
      })
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ""
    const date = new Date(timestamp)
    return date.toLocaleDateString() + " " + date.toLocaleTimeString()
  }

  const columns = useMemo(
    () => [
      {
        header: t("Number"),
        accessorKey: "advanceNumber",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("Date"),
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        header: t("Description"),
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ cell, row }) => {
          const isReturned = row.original.wasReturned
          return (
            <span style={{ textDecoration: isReturned ? "line-through" : "none" }}>
              {cell.getValue()}
            </span>
          )
        },
      },
      {
        header: t("Amount"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell, row }) => {
          const isReturned = row.original.wasReturned
          return (
            <span style={{ textDecoration: isReturned ? "line-through" : "none" }}>
              ${cell.getValue()}
            </span>
          )
        },
      },
      {
        header: t("Balance Type"),
        accessorKey: "balanceType",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const balanceType = cell.getValue()
          const bgColor = balanceType === "crypto" ? "#17a2b8" : "#007bff"
          return (
            <span
              style={{
                backgroundColor: bgColor,
                color: "white",
                padding: "2px 8px",
                borderRadius: "3px",
                fontSize: "0.85em",
              }}
            >
              {balanceType === "crypto" ? t("Crypto") : t("Fiat")}
            </span>
          )
        },
      },
      {
        header: t("Status"),
        accessorKey: "wasReturned",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const wasReturned = cell.getValue()
          const bgColor = wasReturned ? "#6c757d" : "#28a745"
          return (
            <span
              style={{
                backgroundColor: bgColor,
                color: "white",
                padding: "0.2em 0.5em",
                borderRadius: "4px",
              }}
            >
              {wasReturned ? t("Returned") : t("Active")}
            </span>
          )
        },
      },
      {
        header: t("Actions"),
        id: "actions",
        cell: ({ row }) => {
          const advance = row.original
          return !advance.wasReturned ? (
            <button
              className="btn btn-sm btn-warning"
              onClick={() => handleReturnClick(advance)}
            >
              {t("Return")}
            </button>
          ) : (
            <span></span>
          )
        },
      },
    ],
    [t]
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2>{t("Balance Advances")}</h2>
          <div className="mb-3 d-flex gap-2" style={{ alignItems: "flex-start" }}>
            <input
              type="text"
              className="form-control search-box"
              placeholder={`${t("Search by description, amount, or any field")}... ${advances.length} ${t("records")}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '500px' }}
            />
            <Button
              color="primary"
              onClick={handleCreateClick}
            >
              <i className="bx bx-plus me-1"></i>
              {t("Add Advance")}
            </Button>
          </div>
          {searchTerm && (
            <small className="text-muted d-block mb-2">
              {t("Showing")} {filteredAdvances.length} {t("of")} {advances.length} {t("advances")}
            </small>
          )}

          {loadingAdvances ? (
            <p>{t("Loading...")}</p>
          ) : (
            <TableContainer
              columns={columns}
              data={filteredAdvances || []}
              isGlobalFilter={false}
              isCustomPageSize={true}
              isPagination={true}
              pagination="pagination"
              paginationWrapper="dataTables_paginate paging_simple_numbers"
              tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            />
          )}
        </Container>

        {/* Create Advance Modal */}
        <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)}>
          <ModalHeader toggle={() => setShowCreateModal(false)}>
            {t("Add Advance")}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="amount">{t("Amount")} (USD)</Label>
              <Input
                type="number"
                id="amount"
                className="form-control"
                placeholder={t("Enter amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="0.01"
              />
            </div>

            <div className="mb-3">
              <Label htmlFor="description">{t("Description")}</Label>
              <Input
                type="textarea"
                id="description"
                className="form-control"
                placeholder={t("e.g. Emergency advance, Temporary balance fix")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
              />
            </div>

            <div className="mb-3">
              <Label>{t("Add to Balance")}</Label>
              <div>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    id="balanceFiat"
                    name="balanceType"
                    className="form-check-input"
                    checked={balanceType === "fiat"}
                    onChange={() => setBalanceType("fiat")}
                  />
                  <Label className="form-check-label" htmlFor="balanceFiat">
                    {t("Fiat Balance")}
                  </Label>
                </div>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    id="balanceCrypto"
                    name="balanceType"
                    className="form-check-input"
                    checked={balanceType === "crypto"}
                    onChange={() => setBalanceType("crypto")}
                  />
                  <Label className="form-check-label" htmlFor="balanceCrypto">
                    {t("Crypto Balance")}
                  </Label>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowCreateModal(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" onClick={handleCreateSubmit}>
              {t("Continue")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
          <ModalHeader toggle={() => setShowConfirmModal(false)}>
            {t("Confirm Advance")}
          </ModalHeader>
          <ModalBody>
            <p>{t("Please confirm the following advance:")}</p>
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <td><strong>{t("Amount")}:</strong></td>
                  <td>${amount}</td>
                </tr>
                <tr>
                  <td><strong>{t("Description")}:</strong></td>
                  <td>{description}</td>
                </tr>
                <tr>
                  <td><strong>{t("Add to")}:</strong></td>
                  <td>{balanceType === "fiat" ? t("Fiat Balance") : t("Crypto Balance")}</td>
                </tr>
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" onClick={handleConfirmCreate} disabled={submitting}>
              {submitting ? t("Submitting...") : t("Confirm")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Return Modal */}
        <Modal isOpen={showReturnModal} toggle={() => setShowReturnModal(false)}>
          <ModalHeader toggle={() => setShowReturnModal(false)}>
            {t("Return Advance - 2FA Required")}
          </ModalHeader>
          <ModalBody>
            <p>{t("A return code has been sent to your Telegram. Please enter the code to confirm the return.")}</p>
            <p className="text-warning">
              <strong>{t("Warning")}:</strong> {t("This will subtract the amount from the balance.")}
            </p>
            {selectedAdvance && (
              <div className="mb-3">
                <p><strong>{t("Advance")} #{selectedAdvance.advanceNumber}</strong></p>
                <p>{t("Amount")}: ${selectedAdvance.amount}</p>
                <p>{t("Description")}: {selectedAdvance.description}</p>
              </div>
            )}
            <div className="mb-3">
              <Label htmlFor="returnCode">{t("Enter Return Code")}</Label>
              <Input
                type="text"
                id="returnCode"
                className="form-control"
                placeholder={t("Enter 5-digit code")}
                value={returnCode}
                onChange={(e) => setReturnCode(e.target.value)}
                maxLength={5}
              />
            </div>
            {returnError && (
              <div className="alert alert-danger">{returnError}</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowReturnModal(false)} disabled={returning}>
              {t("Cancel")}
            </Button>
            <Button color="warning" onClick={handleReturnConfirm} disabled={returning}>
              {returning ? t("Returning...") : t("Confirm Return")}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  )
}

BalanceAdvancesPage.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(BalanceAdvancesPage)
